import {
  UtilizationReportDocument,
  UtilizationReportGroupedByDocument,
  type UtilizationReportGroupedByQuery,
  type UtilizationReportGroupedByQueryVariables,
  type UtilizationReportQuery,
  type UtilizationReportQueryVariables,
  UtilizationReportStatsDocument,
  type UtilizationReportStatsQuery,
  type UtilizationReportStatsQueryVariables,
} from "@src/__generated__/graphql";
import { UtilizationReportBaseStore } from "@src/components/modules/reports/utilization/stores/base-store";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import type { AppStore } from "@src/stores/AppStore";
import { toApiDate } from "@src/utils/dates";
import type { PaginationState } from "@src/utils/mobx/states/PaginationState";
import { action, computed, makeObservable, observable } from "mobx";

export class UtilizationReportStore extends UtilizationReportBaseStore {
  readonly moduleKey: string;

  @observable.ref
  tableStatsData:
    | UtilizationReportStatsQuery["utilizationReportSummary"]
    | undefined = undefined;

  @observable.ref
  tableData: UtilizationReportQuery["utilizationReport"]["data"] = [];

  @observable.ref
  tableGroupedByData: UtilizationReportGroupedByQuery["utilizationReportGroups"]["data"] =
    [];

  private statsFetcher = new FetchHelper<
    UtilizationReportStatsQuery,
    UtilizationReportStatsQueryVariables
  >(UtilizationReportStatsDocument);
  private reportFetcher = new FetchHelper<
    UtilizationReportQuery,
    UtilizationReportQueryVariables
  >(UtilizationReportDocument);
  private reportGroupedByFetcher = new FetchHelper<
    UtilizationReportGroupedByQuery,
    UtilizationReportGroupedByQueryVariables
  >(UtilizationReportGroupedByDocument);

  constructor(appStore: AppStore) {
    const moduleKey = "utilization-report";
    super(moduleKey, appStore, (pagination, groupBy) => {
      this.fetchStatsData();
      if (!groupBy) {
        this.fetchReportData(pagination);
      } else {
        this.fetchReportGroupedByData(pagination);
      }
    });
    this.moduleKey = moduleKey;
    makeObservable(this);
  }

  @computed
  get isStatsLoading() {
    return this.statsFetcher.isLoading.value;
  }

  @computed
  get isReportLoading() {
    return this.reportFetcher.isLoading.value;
  }

  @computed
  get isReportGroupedByLoading() {
    return this.reportGroupedByFetcher.isLoading.value;
  }

  @action.bound
  async fetchStatsData() {
    const [data, error] = await this.statsFetcher.fetch({
      to: toApiDate(this.dateRange.end),
      from: toApiDate(this.dateRange.start),
      dataSource: this.dataSource,
      where: this.where.asWhereParam,
    });

    if (error) return;

    this.tableStatsData = data.utilizationReportSummary;
  }

  @action.bound
  async fetchReportData(pagination: PaginationState) {
    const [data, error] = await this.reportFetcher.fetch({
      to: toApiDate(this.dateRange.end),
      from: toApiDate(this.dateRange.start),
      dataSource: this.dataSource,
      page: pagination.asParams.page,
      first: pagination.asParams.first,
      orderBy: this.orderBy.asWhereParam,
      where: this.where.asWhereParam,
    });

    if (error) return;

    this.tableData = data.utilizationReport.data;
    this.pagination.setFromPaginatorInfo(data.utilizationReport.paginatorInfo);
  }

  @action.bound
  async fetchReportGroupedByData(pagination: PaginationState) {
    if (!this.groupBy) return;

    const [data, error] = await this.reportGroupedByFetcher.fetch({
      to: toApiDate(this.dateRange.end),
      from: toApiDate(this.dateRange.start),
      dataSource: this.dataSource,
      page: pagination.asParams.page,
      first: pagination.asParams.first,
      groupBy: this.groupBy,
      groupOrderBy: this.orderByGrouped.asWhereParam,
      where: this.where.asWhereParam,
    });

    if (error) return;

    this.tableGroupedByData = data.utilizationReportGroups.data;
    this.pagination.setFromPaginatorInfo(
      data.utilizationReportGroups.paginatorInfo,
    );
  }
}
